import { Brain, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Mission } from "../../lib/commandCore";
import { latestStrategyJob, todayLocalKey } from "../../lib/delegationCore";
import { cleanAgentOutput } from "../../lib/missionExecutionCard";
import { AgentJob, AgentPreset, AgentProvider, api, isTauriEnvironment } from "../../lib/tauri";
import { useUiStore } from "../../stores/uiStore";
import { PresetPicker } from "./PresetPicker";

/**
 * Phase 4: 司令塔「作戦をAIに立てさせる」。
 * - ボタン起点のみ（マウント時は listAgentJobs の読み出しだけで、enqueue・gemma 呼び出しはしない）
 * - AI提案は rule v0 の作戦（TodayBrief / PrimaryMissions）を置き換えず、このカードに併記する
 * - 判断ログへの自動記録は JobRunner::record_decision（既存）が行う
 */
export function StrategyCard({ missions }: { missions: Mission[] }) {
  const showToast = useUiStore((s) => s.showToast);
  const [jobs, setJobs] = useState<AgentJob[]>([]);
  const [provider, setProvider] = useState<AgentProvider>("claude");
  const [preset, setPreset] = useState<AgentPreset>("normal");
  const [modelOverride, setModelOverride] = useState("");
  const [effortOverride, setEffortOverride] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const loadJobs = useCallback(async () => {
    try {
      const rows = await api.listAgentJobs?.(50);
      setJobs(rows ?? []);
    } catch (error) {
      console.error("[Flow] listAgentJobs failed", error);
      setJobs([]);
    }
  }, []);

  useEffect(() => {
    void loadJobs();
  }, [loadJobs]);

  // flow://agent-jobs-changed の listen（AgentBoard.tsx:69-85 と同パターン）
  useEffect(() => {
    if (!isTauriEnvironment()) return;
    let unlisten: (() => void) | undefined;
    let disposed = false;
    void import("@tauri-apps/api/event").then(({ listen }) =>
      listen("flow://agent-jobs-changed", () => {
        void loadJobs();
      }).then((fn) => {
        if (disposed) fn();
        else unlisten = fn;
      })
    );
    return () => {
      disposed = true;
      unlisten?.();
    };
  }, [loadJobs]);

  const todayKey = todayLocalKey();
  const strategy = useMemo(() => latestStrategyJob(jobs, todayKey), [jobs, todayKey]);
  const inFlight = strategy?.status === "queued" || strategy?.status === "running";

  const requestStrategy = async () => {
    setSubmitting(true);
    try {
      await api.enqueueStrategyJob?.({
        provider,
        todayKey: todayLocalKey(),
        missions: missions
          .filter((m) => !m.isFallback)
          .map((m) => ({
            title: m.title,
            owner: m.owner,
            action: m.action,
            score: m.score,
            dueLabel: m.dueLabel,
          })),
        preset: preset !== "normal" ? preset : null,
        modelOverride: modelOverride || null,
        effortOverride: effortOverride || null,
      });
      showToast("作戦立案を依頼しました。結果はこのカードと判断ログに載ります");
      // queued 滞留中も inFlight が立つよう即時再取得（イベント頼みだと並列満杯時に二重投入の窓ができる）
      void loadJobs();
    } catch (e) {
      // api は Proxy なので ?. では短絡しない
      const message = e instanceof Error ? e.message : String(e);
      showToast(
        message.includes("Flow api method not found")
          ? "作戦立案はデスクトップ版のみ対応です"
          : message
      );
    } finally {
      setSubmitting(false);
    }
  };

  const body =
    strategy && (strategy.status === "done" || strategy.status === "needs_decision")
      ? cleanAgentOutput(strategy.result)
      : "";

  return (
    <section className="command-card p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Brain className="h-4 w-4 text-brand-600 dark:text-indigo-200" />
        <h3 className="text-sm font-bold">AI提案（作戦）</h3>
        <span className="command-faint text-[11px]">rule v0 の作戦は置き換えません（併記）</span>
        <div className="ml-auto flex flex-col items-end gap-1.5">
          <div className="flex items-center gap-2">
            <select
              aria-label="作戦の委任先"
              value={provider}
              onChange={(e) => {
                setProvider(e.target.value as AgentProvider);
                // M2: provider 切替時に override を残留させない（不正値400・非表示残留の防止）
                setModelOverride("");
                setEffortOverride("");
              }}
              className="rounded border border-border bg-bg px-1.5 py-1 text-[11px] text-muted dark:border-white/15 dark:bg-black/30 dark:text-zinc-200"
            >
              <option value="claude">Claude</option>
              <option value="codex">Codex</option>
            </select>
            <button
              type="button"
              disabled={submitting || inFlight}
              onClick={() => void requestStrategy()}
              className="inline-flex items-center gap-1.5 rounded-md border border-brand-200 bg-brand-50 px-3 py-1.5 text-xs font-semibold text-brand-700 hover:bg-brand-100 disabled:opacity-50 dark:border-indigo-300/30 dark:bg-indigo-400/15 dark:text-indigo-100 dark:hover:bg-indigo-400/25"
            >
              {submitting || inFlight ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Brain className="h-3.5 w-3.5" />
              )}
              作戦をAIに立てさせる
            </button>
          </div>
          <PresetPicker
            provider={provider}
            preset={preset}
            modelOverride={modelOverride}
            effortOverride={effortOverride}
            onPresetChange={setPreset}
            onModelOverrideChange={setModelOverride}
            onEffortOverrideChange={setEffortOverride}
          />
        </div>
      </div>
      {inFlight && (
        <p className="command-dim mt-2 text-xs">作戦立案中…（稼働ボードにも表示されます）</p>
      )}
      {strategy?.status === "failed" && (
        <p className="mt-2 text-xs text-rose-200">
          作戦立案に失敗: {strategy.error.slice(0, 80)} — 再度ボタンから依頼できます
        </p>
      )}
      {body && (
        <div className="mt-2">
          <p
            className={
              expanded
                ? "whitespace-pre-wrap text-sm text-muted dark:text-zinc-200"
                : "line-clamp-4 whitespace-pre-wrap text-sm text-muted dark:text-zinc-200"
            }
          >
            {body}
          </p>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="command-dim mt-1 inline-flex items-center gap-1 text-[11px] hover:text-text dark:hover:text-zinc-200"
          >
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {expanded ? "たたむ" : "全文を見る"}
          </button>
        </div>
      )}
      {!strategy && !inFlight && (
        <p className="command-faint mt-2 text-xs">
          まだ今日のAI作戦はありません。委任先を選んでボタンで依頼できます（開いただけでは何も実行されません）。
        </p>
      )}
    </section>
  );
}
